import {map} from 'lodash';
import {useCallback, useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import * as uuid from 'uuid/v4';

import {settingsApi, userSettingsApi} from 'api';
import {
    ActionButton, AssignmentSelectors, Button, Card, Checkbox, Icon, PredefinedNotifierType, Radio, Toggle,
    UnsavedChangesLeavePrompt,
} from 'components';
import {useApiData, useUserInfoSchema} from 'hooks';
import {CustomField, SessionScreenshot, SessionScreenshotSubject} from 'interfaces';
import {TRANSLATION_KEYS} from 'locale';
import {
    InviteeParticipantRole, LiveHelpSettings, PreSessionFlow as PreSessionFlowType, PreSessionFlowCompletion,
    PreSessionFlowMode, PreSessionFlowModes, PreSessionFormFieldInputType, PreSessionStep, PreSessionStepType,
} from 'types';
import {dataUtils, userInfoUtils} from 'utils';

import {PreSessionStepRow} from './PreSessionStepRow';

const T_KEYS = TRANSLATION_KEYS.USER.CONTENT.CLIENT_SESSION.PRE_SESSION_FLOW;

const ACCOUNT_ASSIGNMENT_KEY = 'account';

const EMPTY_FLOW: PreSessionFlowType = {
    enabled: false,
    steps: [],
};

// A flow saved before modes existed is answered by the form only.
const DEFAULT_MODES: PreSessionFlowModes = {chatbot: false, form: true};

const buildEmptyStep = (): PreSessionStep => ({
    id: uuid(),
    name: '',
    type: PreSessionStepType.Form,
    required: true,
    clientPrompt: '',
    form: {
        fields: [{
            id: uuid(),
            label: '',
            inputType: PreSessionFormFieldInputType.Text,
            required: true,
        }],
    },
});

export const PreSessionFlow = () => {
    const {t} = useTranslation();
    const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);
    const [flow, setFlow] = useState<PreSessionFlowType>(EMPTY_FLOW);
    const [initialFlow, setInitialFlow] = useState<PreSessionFlowType>(EMPTY_FLOW);

    const handleFetchSuccess = useCallback((liveHelp: LiveHelpSettings) => {
        const incoming = liveHelp?.widget?.preSessionFlow ?? EMPTY_FLOW;
        setFlow(incoming);
        setInitialFlow(incoming);
    }, []);

    const {send: fetchLiveHelp, loading: fetchLoading} = useApiData(
        userSettingsApi.getLiveHelp,
        {notificationType: PredefinedNotifierType.FetchError, onSuccess: handleFetchSuccess}
    );

    const {send: saveLiveHelp, loading: saveLoading} = useApiData(
        userSettingsApi.updateLiveHelp,
        {notificationType: PredefinedNotifierType.Save, onSuccess: handleFetchSuccess}
    );

    // Per-group screenshot configurations and user-info schema feed the Step editor
    // (screenshot Select + Form field-path Select).
    const {getUserInfoFields, getControlOnlyFields} = useUserInfoSchema({
        role: InviteeParticipantRole.Client, fetchOnInit: true,
    });
    const {send: fetchScreenshots, data: screenshotsResponse} = useApiData(settingsApi.getScreenshots);

    useEffect(() => {
        fetchLiveHelp(selectedGroupId);
    }, [fetchLiveHelp, selectedGroupId]);

    useEffect(() => {
        fetchScreenshots();
    }, [fetchScreenshots]);

    const editingDisabled = fetchLoading || saveLoading;
    const isDirty = useMemo(() => !dataUtils.isEqualValues(flow, initialFlow), [flow, initialFlow]);

    const userInfoFields = useMemo<CustomField[]>(() => {
        const assignment = {groupId: selectedGroupId || undefined};
        const top = map(getUserInfoFields(assignment), (field) => ({
            ...field,
            title: userInfoUtils.getUserInfoFieldLabel(field, t),
        }));
        // controlOnlyInfo children are nested under an Object field that getUserInfoFields filters
        // out — surface them as `controlOnlyInfo.<name>` so they're selectable form-field paths.
        const controlOnly = map(getControlOnlyFields(assignment), (field) => ({
            ...field,
            name: `controlOnlyInfo.${field.name}`,
            title: userInfoUtils.getUserInfoFieldLabel(field, t),
        }));
        return [...top, ...controlOnly];
    }, [getUserInfoFields, getControlOnlyFields, selectedGroupId, t]);

    const screenshots = useMemo<SessionScreenshot[]>(() => {
        const raw = screenshotsResponse?.[selectedGroupId || ACCOUNT_ASSIGNMENT_KEY]?.screenshots ?? [];
        return raw.filter((s) =>
            s.subject === SessionScreenshotSubject.ParticipantVideo
            && (!s.role || s.role === InviteeParticipantRole.Client));
    }, [screenshotsResponse, selectedGroupId]);

    const handleEnabledChange = useCallback((enabled: boolean) => {
        setFlow((prev) => ({...prev, enabled}));
    }, []);

    const modes = useMemo<PreSessionFlowModes>(
        () => flow.modes ?? DEFAULT_MODES,
        [flow.modes]
    );

    const handleModeChange = useCallback((mode: keyof PreSessionFlowModes) => {
        setFlow((prev) => {
            const currentModes = prev.modes ?? DEFAULT_MODES;
            const nextModes = {...currentModes, [mode]: !currentModes[mode]};
            return nextModes.chatbot || nextModes.form ? {...prev, modes: nextModes} : prev;
        });
    }, []);

    const defaultMode = useMemo(() => flow.defaultMode && modes[flow.defaultMode]
        ? flow.defaultMode : modes.chatbot ? PreSessionFlowMode.Chatbot : PreSessionFlowMode.Form,
    [flow.defaultMode, modes]);

    const defaultModeOptions = useMemo(() => [
        {id: PreSessionFlowMode.Chatbot, name: t(T_KEYS.MODE_CHATBOT)},
        {id: PreSessionFlowMode.Form, name: t(T_KEYS.MODE_FORM)},
    ], [t]);

    const handleDefaultModeChange = useCallback((value: PreSessionFlowMode) => {
        setFlow((prev) => ({...prev, defaultMode: value}));
    }, []);

    const onCompleteOptions = useMemo(() => [
        {id: PreSessionFlowCompletion.Escalate, name: t(T_KEYS.ON_COMPLETE_ESCALATE)},
        {id: PreSessionFlowCompletion.End, name: t(T_KEYS.ON_COMPLETE_END)},
    ], [t]);

    // Absent means escalate: that is what a flow did before the chatbot could take over.
    const onComplete = flow.onComplete === PreSessionFlowCompletion.Chat
        ? PreSessionFlowCompletion.End : flow.onComplete ?? PreSessionFlowCompletion.Escalate;

    const handleOnCompleteChange = useCallback((value: PreSessionFlowCompletion) => {
        setFlow((prev) => ({...prev, onComplete: value}));
    }, []);

    const handleStepChange = useCallback((stepIndex: number, next: PreSessionStep) => {
        setFlow((prev) => {
            const steps = [...(prev.steps ?? [])];
            steps[stepIndex] = next;
            return {...prev, steps};
        });
    }, []);

    const handleStepRemove = useCallback((stepIndex: number) => {
        setFlow((prev) => {
            const steps = [...(prev.steps ?? [])];
            steps.splice(stepIndex, 1);
            return {...prev, steps};
        });
    }, []);

    const handleStepMove = useCallback((stepIndex: number, delta: -1 | 1) => {
        setFlow((prev) => {
            const steps = [...(prev.steps ?? [])];
            const target = stepIndex + delta;
            if (target < 0 || target >= steps.length) {
                return prev;
            }
            [steps[stepIndex], steps[target]] = [steps[target], steps[stepIndex]];
            return {...prev, steps};
        });
    }, []);

    const handleAddStep = useCallback(() => {
        setFlow((prev) => ({...prev, steps: [...(prev.steps ?? []), buildEmptyStep()]}));
    }, []);

    const handleSave = useCallback(() => {
        const widgetUpdate = {preSessionFlow: {...flow, modes, onComplete, defaultMode}} as LiveHelpSettings['widget'];
        saveLiveHelp({groupId: selectedGroupId, liveHelp: {widget: widgetUpdate}});
    }, [flow, modes, onComplete, defaultMode, saveLiveHelp, selectedGroupId]);

    return (
        <div className='tw-max-w-screen-md'>
            <AssignmentSelectors
                selectedGroupId={selectedGroupId}
                onChange={(groupId) => setSelectedGroupId(groupId)}
            >
                {({renderGroup}) => (
                    <div className='tw-w-full md:tw-w-64 tw-flex-shrink-1'>{renderGroup()}</div>
                )}
            </AssignmentSelectors>

            <Card className='tw-mt-4'>
                <div className='tw-flex tw-flex-col tw-gap-5'>
                    <p className='tw-text-sm tw-text-default-dimmed tw-m-0'>
                        {t(T_KEYS.DESCRIPTION)}
                    </p>

                    <Toggle
                        name='preSessionFlow.enabled'
                        checked={flow.enabled}
                        label={t(T_KEYS.ENABLED)}
                        disabled={editingDisabled}
                        onChange={handleEnabledChange}
                    />

                    <div className='tw-flex tw-flex-wrap tw-items-center tw-gap-x-6 tw-gap-y-2'>
                        <span className='tw-text-sm tw-text-default'>{t(T_KEYS.MODES_LABEL)}</span>
                        <Checkbox
                            name='preSessionFlow-mode-chatbot'
                            size='small'
                            checked={modes.chatbot}
                            label={t(T_KEYS.MODE_CHATBOT)}
                            disabled={editingDisabled || (modes.chatbot && !modes.form)}
                            onChange={() => handleModeChange('chatbot')}
                        />
                        <Checkbox
                            name='preSessionFlow-mode-form'
                            size='small'
                            checked={modes.form}
                            label={t(T_KEYS.MODE_FORM)}
                            disabled={editingDisabled || (modes.form && !modes.chatbot)}
                            onChange={() => handleModeChange('form')}
                        />
                    </div>

                    {modes.chatbot && modes.form && (
                        <div className='tw-flex tw-flex-wrap tw-items-center tw-gap-x-6 tw-gap-y-2'>
                            <span className='tw-text-sm tw-text-default'>{t(T_KEYS.DEFAULT_MODE_LABEL)}</span>
                            {defaultModeOptions.map((option) => (
                                <Radio
                                    key={option.id}
                                    id={`preSessionFlow-defaultMode-${option.id}`}
                                    name='preSessionFlow.defaultMode'
                                    value={option.id}
                                    label={option.name}
                                    checked={defaultMode === option.id}
                                    disabled={editingDisabled}
                                    onSelect={handleDefaultModeChange}
                                />
                            ))}
                        </div>
                    )}

                    <div className='tw-flex tw-flex-wrap tw-items-center tw-gap-x-6 tw-gap-y-2'>
                        <span className='tw-text-sm tw-text-default'>{t(T_KEYS.ON_COMPLETE_LABEL)}</span>
                        {onCompleteOptions.map((option) => (
                            <Radio
                                key={option.id}
                                id={`preSessionFlow-onComplete-${option.id}`}
                                name='preSessionFlow.onComplete'
                                value={option.id}
                                label={option.name}
                                checked={onComplete === option.id}
                                disabled={editingDisabled}
                                onSelect={handleOnCompleteChange}
                            />
                        ))}
                    </div>

                    <div className='tw-flex tw-flex-col tw-gap-3'>
                        <div className='tw-flex tw-items-center tw-justify-between tw-pb-2 tw-border-b tw-border-default'>
                            <h3 className='tw-ts-h4-bold tw-text-dark tw-m-0'>{t(T_KEYS.STEPS_LABEL)}</h3>
                            {(flow.steps ?? []).length > 0 && (
                                <ActionButton
                                    action='create'
                                    width='auto'
                                    disabled={editingDisabled}
                                    onClick={handleAddStep}
                                >
                                    {t(T_KEYS.STEPS_ADD)}
                                </ActionButton>
                            )}
                        </div>

                        {(flow.steps ?? []).length === 0 ? (
                            <div
                                className='tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-3
                                    tw-py-10 tw-px-4 tw-border tw-border-dashed tw-border-default tw-rounded'
                            >
                                <Icon name='file-text' size='xl' color='default-dimmed' />
                                <div className='tw-ts-h5-bold tw-text-default'>
                                    {t(T_KEYS.STEPS_EMPTY_TITLE)}
                                </div>
                                <p className='tw-text-sm tw-text-default-dimmed tw-text-center tw-max-w-sm tw-m-0'>
                                    {t(T_KEYS.STEPS_EMPTY_DESCRIPTION)}
                                </p>
                                <ActionButton
                                    action='create'
                                    width='auto'
                                    disabled={editingDisabled}
                                    onClick={handleAddStep}
                                >
                                    {t(T_KEYS.STEPS_ADD)}
                                </ActionButton>
                            </div>
                        ) : (
                            <div>
                                {flow.steps.map((step, idx) => (
                                    <PreSessionStepRow
                                        key={step.id}
                                        step={step}
                                        index={idx}
                                        total={flow.steps.length}
                                        isLast={idx === flow.steps.length - 1}
                                        disabled={editingDisabled}
                                        userInfoFields={userInfoFields}
                                        screenshots={screenshots}
                                        onChange={(next) => handleStepChange(idx, next)}
                                        onRemove={() => handleStepRemove(idx)}
                                        onMoveUp={() => handleStepMove(idx, -1)}
                                        onMoveDown={() => handleStepMove(idx, 1)}
                                    />
                                ))}
                            </div>
                        )}
                    </div>

                    <div className='tw-flex tw-justify-end tw-pt-4 tw-border-t tw-border-default'>
                        <Button
                            color='primary'
                            disabled={editingDisabled || !isDirty}
                            loading={saveLoading}
                            onClick={handleSave}
                        >
                            {t(T_KEYS.SAVE)}
                        </Button>
                    </div>
                </div>
            </Card>
            <UnsavedChangesLeavePrompt values={flow} initialValues={initialFlow} />
        </div>
    );
};
